import { EyeOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Card, Flex, Input, Select, Space, Table, Tag } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import type { SorterResult } from 'antd/es/table/interface'
import dayjs from 'dayjs'
import { BoosterProgramItem, SearchBoosterProgramParams } from 'Modules'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  BOOSTER_PROGRAM_STATUS_CONFIG,
  BOOSTER_PROGRAM_STATUS_OPTIONS,
  BOOSTER_PROGRAM_TARGET_USER_CONFIG,
  BOOSTER_PROGRAM_TARGET_USER_OPTIONS,
} from 'constants/booster-program'
import { FEATURE } from 'constants/feature'
import { DEFAULT_PAGE_OPTIONS } from 'constants/pagination'
import { useBoosterPrograms } from 'hooks/booster-program'
import { useDebounce } from 'hooks/use-debounce'
import { usePermission } from 'hooks/use-permission'
import useSearchParams from 'hooks/use-search-params'
import { DeleteProgramModal } from 'modules/booster-program/delete-program-modal'
import { PAGES } from 'routes'

export const BoosterProgramTable = () => {
  const navigate = useNavigate()
  const { canCRUD } = usePermission()
  const canEdit = canCRUD(FEATURE.BOOSTER_PROGRAM)

  const { searchParams, setSearchParams, initPagination, onSearch } =
    useSearchParams<SearchBoosterProgramParams>({})
  const [searchValue, setSearchValue] = useState('')
  const debouncedSearch = useDebounce(searchValue, 400) as string
  const [programToDelete, setProgramToDelete] = useState<BoosterProgramItem | null>(null)

  // Keep latest onSearch in a ref so the debounce effect can call it without
  // re-running on every render (useSearchParams returns a new fn each time).
  const onSearchRef = useRef(onSearch)
  onSearchRef.current = onSearch

  // Skip the initial mount so we don't fire an unnecessary refetch on load.
  const didMountRef = useRef(false)
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      return
    }
    const keyword = debouncedSearch.trim()
    onSearchRef.current({ keyword: keyword || undefined })
  }, [debouncedSearch])

  const { programs, total, isLoading } = useBoosterPrograms(searchParams)

  const columns: ColumnsType<BoosterProgramItem> = useMemo(() => {
    const sortOrderFor = (field: string) => {
      if (searchParams.sort === `${field}:asc`) return 'ascend' as const
      if (searchParams.sort === `${field}:desc`) return 'descend' as const
      return null
    }
    return [
      {
        title: 'Created Date',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 170,
        sorter: true,
        sortOrder: sortOrderFor('createdAt'),
        render: (value: string) => dayjs(value).format('DD/MM/YYYY'),
      },
      {
        title: 'Program Title',
        dataIndex: 'title',
        key: 'title',
        width: 220,
        render: (value: string) => <span className="booster-program-table__title">{value}</span>,
      },
      {
        title: 'Target Users',
        dataIndex: 'targetUsers',
        key: 'targetUsers',
        width: 150,
        render: (value: BoosterProgramItem['targetUsers']) =>
          BOOSTER_PROGRAM_TARGET_USER_CONFIG[value],
      },
      {
        title: 'Program Period',
        key: 'period',
        width: 180,
        render: (_, record) => (
          <div className="booster-program-table__period">
            <div>{dayjs(record.startDate).format('MMM D, YYYY')} -</div>
            <div>{dayjs(record.endDate).format('MMM D, YYYY')}</div>
          </div>
        ),
      },
      {
        title: 'Enrolled Users',
        dataIndex: 'enrolledUsers',
        key: 'enrolledUsers',
        width: 170,
        sorter: true,
        sortOrder: sortOrderFor('enrolledUsers'),
        render: (value: number) => new Intl.NumberFormat('en-US').format(value),
      },
      {
        title: 'Milestones',
        dataIndex: 'milestones',
        key: 'milestones',
        width: 110,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (status: BoosterProgramItem['status']) => {
          const config = BOOSTER_PROGRAM_STATUS_CONFIG[status]
          return (
            <Tag
              className="booster-program-table__status-tag"
              style={{
                color: config.color,
                backgroundColor: config.background,
                borderColor: 'transparent',
              }}
            >
              {config.label}
            </Tag>
          )
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 220,
        align: 'left',
        render: (_, record) => {
          const canDelete = canEdit && (record.status === 'DRAFT' || record.status === 'SCHEDULED')
          return (
            <Space wrap={false}>
              <Button
                type="primary"
                icon={<EyeOutlined />}
                className="booster-program-table__view-btn"
                onClick={() => {
                  if (record.status === 'DRAFT') {
                    navigate(`${PAGES.BOOSTER_PROGRAM_CREATE}?id=${record.id}`)
                    return
                  }
                  navigate(`${PAGES.BOOSTER_PROGRAM}/${record.id}`)
                }}
              >
                View
              </Button>
              {canDelete && (
                <Button color="danger" variant="solid" onClick={() => setProgramToDelete(record)}>
                  Delete
                </Button>
              )}
            </Space>
          )
        },
      },
    ]
  }, [navigate, searchParams.sort, canEdit])

  const showingCount = programs.length

  const handleSearch = () => {
    const keyword = searchValue.trim()
    onSearch({ keyword: keyword || undefined })
  }

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<BoosterProgramItem> | SorterResult<BoosterProgramItem>[]
  ) => {
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter
    const sort =
      singleSorter?.field && singleSorter.order
        ? `${String(singleSorter.field)}:${singleSorter.order === 'ascend' ? 'asc' : 'desc'}`
        : undefined

    setSearchParams((prev) => ({
      ...prev,
      page: pagination.current,
      limit: pagination.pageSize,
      sort,
    }))
  }

  return (
    <Card className="booster-program-table" title="Booster Program List">
      <div className="booster-program-table__toolbar">
        <Flex gap={16} wrap="wrap" align="center" justify="space-between">
          <Flex gap={12} wrap="wrap" style={{ flex: 1, minWidth: 320 }}>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search by program name"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
              style={{ flex: 1, maxWidth: 448, minWidth: 240 }}
            />
          </Flex>
          <Flex gap={12} wrap="wrap">
            <Select<BoosterProgramItem['targetUsers'] | 'ANY'>
              options={[{ value: 'ANY', label: 'All' }, ...BOOSTER_PROGRAM_TARGET_USER_OPTIONS]}
              value={searchParams.targetUsers ?? 'ANY'}
              onChange={(value) => onSearch({ targetUsers: value === 'ANY' ? undefined : value })}
              style={{ width: 180 }}
            />
            <Select<BoosterProgramItem['status'] | 'ALL'>
              options={[{ value: 'ALL', label: 'All Status' }, ...BOOSTER_PROGRAM_STATUS_OPTIONS]}
              value={searchParams.status ?? 'ALL'}
              onChange={(value) => onSearch({ status: value === 'ALL' ? undefined : value })}
              style={{ width: 160 }}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={!canEdit}
              onClick={() => navigate(PAGES.BOOSTER_PROGRAM_CREATE)}
            >
              Create Booster Program
            </Button>
          </Flex>
        </Flex>

        <div className="booster-program-table__summary">
          Showing <strong>{showingCount}</strong> of <strong>{total}</strong> programs
        </div>
      </div>

      <Table<BoosterProgramItem>
        columns={columns}
        dataSource={programs}
        rowKey={(record) => record.id}
        loading={isLoading}
        pagination={{
          ...initPagination(total),
          showSizeChanger: true,
          pageSizeOptions: DEFAULT_PAGE_OPTIONS,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1100 }}
      />

      <DeleteProgramModal
        open={!!programToDelete}
        program={programToDelete}
        onClose={() => setProgramToDelete(null)}
      />
    </Card>
  )
}
